// =============================================================================
// src/views/mapa-eleitoral/overlays.ts — Floats sobre o mapa (legenda, ano, busca).
//
// Proveniencia: assets/unified-app.js Sections.mapaEleitoral._buildOverlays /
//   _yearSub / _updateLegend / _updateInstant (linhas 900-1035). Os 3 overlays
//   (uo-legend, uo-instant, uo-search) sao injetados em #mapOverlays e removidos
//   em deactivate; mesmos ids/classes/markup do original.
//
// SRP: so a camada de overlays. O play/timeline vive em playback.ts; estilos em
//   styles.ts. Nenhuma chamada Leaflet aqui (apenas DOM em #mapOverlays).
// =============================================================================

import type { ElectoralYear, LegendLayerKey, LegendEntry } from '../../app/types';
import { MANAUS_LEGEND_DATA } from '../../data';

/** Ids dos overlays injetados, na ordem de _buildOverlays (legenda, ano, busca). */
export const MAPA_OVERLAY_IDS: readonly string[] = ['uo-legend', 'uo-instant', 'uo-search'];

/**
 * Subtitulo do ano (DATA._yearSub). 2024 e fixo "municipal · 2º turno"; anos
 * multiplos de 4 sao "municipal", os demais "estadual/federal". Logica preservada.
 */
export function yearSubtitleOf(year: ElectoralYear): string {
  if (year === '2024') return 'municipal · 2º turno';
  return parseInt(year, 10) % 4 === 0 ? 'municipal' : 'estadual/federal';
}

/** Linhas de swatch+rotulo da legenda (DATA.legendData[layer].items). */
function renderLegendItems(legend: LegendEntry): string {
  return legend.items
    .map(
      (legendItem) =>
        '<div class="u-legend-item"><div class="u-legend-swatch" style="background:' +
        legendItem[1] +
        '"></div>' +
        legendItem[0] +
        '</div>',
    )
    .join('');
}

/** Float da legenda flutuante (#uo-legend) com titulo e corpo da camada atual. */
function renderLegendFloat(legend: LegendEntry): string {
  return (
    '<div class="u-legend-float" id="uo-legend">' +
    '<div class="u-legend-title" id="uo-legend-title">' +
    legend.title +
    '</div>' +
    '<div id="uo-legend-body">' +
    renderLegendItems(legend) +
    '</div></div>'
  );
}

/** Float do ano instantaneo (#uo-instant) com numero e subtitulo. */
function renderInstantFloat(year: ElectoralYear): string {
  return (
    '<div class="u-instant-label" id="uo-instant">' +
    '<div class="u-year-num" id="uo-instant-year">' +
    year +
    '</div>' +
    '<div class="u-year-sub" id="uo-instant-sub">' +
    yearSubtitleOf(year) +
    '</div>' +
    '</div>'
  );
}

/** Float de busca (#uo-search) — placeholder estatico do original. */
function renderSearchFloat(): string {
  return (
    '<div class="u-search-float" id="uo-search">' +
    '<i class="ti ti-search"></i><span>Buscar bairro, zona, candidato…</span></div>'
  );
}

/**
 * Injeta legenda+ano+busca em #mapOverlays (replica _buildOverlays).
 * Early-return se o mount nao existir (paridade: o original tambem checa overlays).
 */
export function buildMapaOverlays(currentLayer: LegendLayerKey, currentYear: ElectoralYear): void {
  const legend = MANAUS_LEGEND_DATA[currentLayer];
  const overlaysMount = document.getElementById('mapOverlays');
  if (!overlaysMount) return;
  const overlaysMarkup =
    renderLegendFloat(legend) + renderInstantFloat(currentYear) + renderSearchFloat();
  overlaysMount.insertAdjacentHTML('beforeend', overlaysMarkup);
}

/** Re-renderiza titulo+corpo da legenda apos troca de camada (replica _updateLegend). */
export function updateLegendFloat(currentLayer: LegendLayerKey): void {
  const legend = MANAUS_LEGEND_DATA[currentLayer];
  const legendTitle = document.getElementById('uo-legend-title');
  const legendBody = document.getElementById('uo-legend-body');
  if (legendTitle) legendTitle.textContent = legend.title;
  if (legendBody) legendBody.innerHTML = renderLegendItems(legend);
}

/** Atualiza numero+subtitulo do ano apos avanço da timeline (replica _updateInstant). */
export function updateInstantFloat(currentYear: ElectoralYear): void {
  const instantYear = document.getElementById('uo-instant-year');
  const instantSub = document.getElementById('uo-instant-sub');
  if (instantYear) instantYear.textContent = currentYear;
  if (instantSub) instantSub.textContent = yearSubtitleOf(currentYear);
}

/** Remove os 3 overlays do DOM (replica o forEach de remoção em deactivate). */
export function removeMapaOverlays(): void {
  MAPA_OVERLAY_IDS.forEach((overlayId) => {
    const overlayNode = document.getElementById(overlayId);
    if (overlayNode) overlayNode.remove();
  });
}
